import { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Info, CheckCircle, AlertTriangle, AlertCircle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { WelcomeMessage } from '@/hooks/useWelcomeMessages';

interface WelcomeMessageModalProps {
  message: WelcomeMessage | null;
  isOpen: boolean;
  onClose: () => void;
  onDismiss: () => void;
  onView: () => void;
}

const styleConfig = {
  info: {
    accent: 'bg-blue-500',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    Icon: Info,
  },
  success: {
    accent: 'bg-green-500',
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    Icon: CheckCircle,
  },
  warning: {
    accent: 'bg-amber-500',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    Icon: AlertTriangle,
  },
  important: {
    accent: 'bg-red-500',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    Icon: AlertCircle,
  },
};

export function WelcomeMessageModal({
  message,
  isOpen,
  onClose,
  onDismiss,
  onView,
}: WelcomeMessageModalProps) {
  const hasTrackedView = useRef(false);

  // Track view when modal opens
  useEffect(() => {
    if (isOpen && message && !hasTrackedView.current) {
      onView();
      hasTrackedView.current = true;
    }
    
    // Reset when message changes
    if (!isOpen) {
      hasTrackedView.current = false;
    }
  }, [isOpen, message, onView]);

  if (!message) return null;

  const config = styleConfig[message.style];
  const Icon = config.Icon;

  const handleDismiss = () => {
    onDismiss();
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 grid w-full max-w-3xl translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
            "p-0 overflow-hidden"
          )}
        >
          {/* Style accent bar */}
          <div className={cn('h-1.5 w-full', config.accent)} />
          
          <div className="p-6">
            <DialogHeader className="pb-4">
              <div className="flex items-start gap-4">
                <div className={cn('rounded-full p-2.5', config.iconBg)}>
                  <Icon className={cn('h-5 w-5', config.iconColor)} />
                </div>
                <div className="flex-1 pt-1">
                  <DialogTitle className="text-xl font-semibold leading-tight">
                    {message.title}
                  </DialogTitle>
                </div>
              </div>
            </DialogHeader>

            <div className="py-2 pl-14">
              <div className="prose prose-sm max-w-none text-foreground prose-headings:text-foreground prose-a:text-primary prose-strong:text-foreground">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {message.body}
                </ReactMarkdown>
              </div>
            </div>

            <DialogFooter className="pt-4 mt-4 border-t">
              {message.display_mode === 'dismissable' ? (
                <div className="flex gap-3 w-full justify-end">
                  <Button variant="outline" onClick={handleClose}>
                    Remind me later
                  </Button>
                  <Button onClick={handleDismiss}>
                    Got it, don't show again
                  </Button>
                </div>
              ) : (
                <Button onClick={handleClose}>
                  Close
                </Button>
              )}
            </DialogFooter>
          </div>
          
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </Dialog>
  );
}
